/* eslint-disable no-param-reassign */
import React, {
  useEffect, Fragment, useState, useMemo,
} from 'react';
import {
  withFormik, Form, Field, ErrorMessage,
} from 'formik';
import { connect, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import SearchIcon from '@material-ui/icons/Search';
import CloseIcon from '@material-ui/icons/Close';
import Checkbox from '@material-ui/core/Checkbox';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import {
  Box,
  Dialog,
  Divider,
  Grid, Typography,
  useMediaQuery,
} from '@material-ui/core';
import { useTheme, withStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import i18n from '~/i18n';
import {
  Card, CardBody, CardHeader, CardIcon,
} from '../../../components/Card';
import PF2MSearchButton from '../../../components/PF2MSearchButton';
import PF2MClearFiltersButton from '../../../components/PF2MClearFiltersButton';
import PF2MDatepickerNew from '../../../components/PF2MDatepickerNew';
import styles from './styles';
import usePlatformConfiguration, {
  usePlatformConfigurationAuth,
} from '~/services/platformConfiguration';
import {
  searchTransportList,
  setFilters,
  resetDirtyFlags,
} from '~/store/dailyparts/actions';
import {
  getEquipmentTypes,
  getEquipmentsGroups,
  getEquipments,
  getOperatorsGroups,
  getOperators,
  getMaterials,
  getElements,
  getTurns,
  getCodeGroups,
  getCodes,
  getSubElements,
  getPlatformConfiguration,
  getHandlingTypes,
} from '~/store/manager/actions';
import PF2MButton from '~/components/PF2MButton';
import PF2MSearchSelectOutlined from '~/components/PF2MSearchSelectOutlined';
import PF2MAutocomplete from '~/components/PF2MAutocomplete';
import { newMomentDate } from '~/utils/moment';

const SearchForm = ({
  values,
  handleSubmit,
  classes,
  isValid,
  turns,
  equipmentTypes,
  equipmentGroups,
  equipments,
  operatorsGroups,
  operators,
  materials,
  elements,
  subElements,
  codeGroups,
  codes,
  handlingTypes,
  isModal,
  onClose,
  resetForm,
  open,
}) => {
  const { t: translate } = useTranslation();
  const [dataLoaded, setDataLoaded] = useState(false);
  const [searchDailyParts, setSearchDailyParts] = useState(true);
  const dispatch = useDispatch();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const { value: hideInactive } = usePlatformConfiguration('hide_inactive');
  // Quando a chave hideInactive for 0, showAll será verdadeiro
  // Quando a chave hideInactive for 1, showAll será falso
  const showAll = parseInt(hideInactive) ? 0 : 1;

  useEffect(() => {
    async function fetchData() {
      dispatch(getTurns(true));
      dispatch(getEquipmentTypes(true));
      dispatch(getEquipmentsGroups(true));
      dispatch(getEquipments(true));
      dispatch(getOperatorsGroups());
      dispatch(getOperators(true));
      dispatch(getMaterials(true));
      dispatch(getElements(true));
      dispatch(getSubElements(true));
      dispatch(getCodeGroups(true));
      dispatch(getCodes(true));
      dispatch(getPlatformConfiguration());
      dispatch(getHandlingTypes(true));
    }
    if (!dataLoaded) {
      fetchData();
      setDataLoaded(true);
    }
  }, [dataLoaded, dispatch]);

  const { value: enableShowOnlyReservedEquips } = usePlatformConfigurationAuth('enable_equip_replaced');

  const renderMenuItem = (key, value, text, active) => (
    {
      label: text,
      value,
      inactiveCondition: active !== undefined && !active,
      tooltip: active !== undefined && !active ? translate('common:Inactive') : '',
    }
  );

  const renderTurns = () => [renderMenuItem('turn_0', 0, translate('common:Select'))]
    .concat(...turns
      .filter(s => showAll || s.active)
      .filter(s => s.name)
      .map(s => renderMenuItem(s.id, s.id, `${s.turn_group_name} - ${s.name}`, s.active)));

  const renderEquipmentTypes = () => [renderMenuItem('equipmentType_0', 0, translate('common:Select'))]
    .concat(...equipmentTypes
      .filter(e => showAll || e.active)
      .map(e => renderMenuItem(e.id, e.id, e.name, e.active)));

  const renderEquipmentGroups = () => equipmentGroups
    .filter(e => showAll || e.active)
    .filter(e => !values.equipmentType || e.id_equipament === values.equipmentType)
    .map(e => renderMenuItem(e.id, e.id, e.name, e.active));

  const renderEquipments = () => equipments
    .filter(e => showAll || e.active)
    .filter((e) => {
      if (values.equipmentGroup.length > 0) {
        return !values.equipmentType || values.equipmentGroup.includes(e.id_group);
      }

      return !values.equipmentType || e.id_equip === values.equipmentType;
    })
    .map(e => renderMenuItem(e.id, e.id, e.name, e.active));

  const renderOperatorsGroups = () => [renderMenuItem('operatorGroup_0', 0, translate('common:Select'))]
    .concat(...operatorsGroups
      .filter(e => showAll || e.active)
      .filter(e => e.name)
      .map(e => renderMenuItem(e.id, e.id, e.name, e.active)));

  const renderOperators = () => [renderMenuItem('operator_0', 0, translate('common:Select'))]
    .concat(...operators
      .filter(e => showAll || e.active)
      .filter(e => e.name && (!values.operatorGroup || e.id_group === values.operatorGroup))
      .map(e => renderMenuItem(e.id_operator, e.id_operator, e.name, e.active)));

  const renderMaterials = () => [renderMenuItem('material_0', 0, translate('common:Select'))]
    .concat(...materials
      .filter(e => showAll || e.active)
      .filter(m => m.id > 0)
      .map(e => renderMenuItem(e.id, e.id, e.name, e.active)));

  const renderOrigins = () => [renderMenuItem('origin_0', 0, translate('common:Select'))]
    .concat(
      ...elements
        .filter(e => showAll || e.active)
        .sort((i, k) => (i.name < k.name ? 1 : -1)) // alphanumeric sorting
        .sort(i => i.active * -1) // move inactive items to the end of the array
        .map(e => renderMenuItem(e.id, e.id, e.name, e.active)),
    );

  const renderSubOrigins = filterKey => [renderMenuItem('suborigin_0', 0, translate('common:Select'))]
    .concat(
      ...subElements
        .filter(e => showAll || e.active)
        .filter(e => (e.id_element === values[filterKey]))
        .sort((i, k) => (i.name < k.name ? 1 : -1)) // alphanumeric sorting
        .sort(i => i.active * -1) // move inactive items to the end of the array
        .map(e => renderMenuItem(e.id, e.id, e.name, e.active)),
    );

  const renderCodesTypes = () => [renderMenuItem('codetypes_0', 0, translate('common:Select'))]
    .concat(
      ...[
        { id: '00', name: translate('common:CodeMaintenanceType') }, // essa linha se encontra dessa forma para que haja diferença com o valor que se encontra na origem
        { id: '1', name: translate('common:CodeOperationalStopType') },
        { id: '2', name: translate('common:CodeEffectiveType') },
        { id: '3', name: translate('common:CodeInactiveType') },
        { id: '4', name: translate('common:CodeIdleType') },
      ]
        .map(e => renderMenuItem(e.id, e.id, e.name)),
    );

  const renderCodeGroups = () => [renderMenuItem('codegroups_0', 0, translate('common:Select'))]
    .concat(
      ...codeGroups
        .filter(e => showAll || e.active)
        .filter(e => typeof values.codeType === 'string' && e.id_equip === values.equipmentType && (!values.codeType || e.code_type === parseInt(values.codeType, 10)))
        .map(e => renderMenuItem(e.id.toString(), e.id.toString(), e.name, e.active)),
    );
  const renderCodes = () => [renderMenuItem('codes_0', 0, translate('common:Select'))]
    .concat(
      ...codes
        .filter(e => showAll || e.active)
        .filter(e => typeof values.codeGroup === 'string' && e.id_equip === values.equipmentType && (!values.codeGroup || e.id_group === parseInt(values.codeGroup, 10)))
        .map(e => renderMenuItem(e.id, e.id, e.name, e.active)),
    );

  const renderDate = (field) => {
    const format = i18n.t('date:DateFormat');

    return (
      <Field
        name={field}
        data-testid={field}
        isDateTime
        format={`${format} HH:mm:ss`}
        views={['hours', 'minutes', 'seconds']}
        noParseDate
        component={PF2MDatepickerNew}
        style={{
          width: '100%',
          border: '1px solid #d9d9d9',
          borderRadius: 4,
          padding: '4px 11px',
        }}
      />
    );
  };

  const validateSelection = ({
    form, name, value, isMulti = false,
  }) => {
    // Seta os elementos subsequentes como 0, caso ocorra mudanca do elemento Pai
    // TO DO, fazer o mesmo para os outros elementos do modal
    if (isMulti) {
      const onlyValues = value?.map(v => v.value);
      return form.setFieldValue(name, onlyValues, true);
    }

    if (name === 'equipmentType') {
      form.values.equipmentGroup = [];
      form.values.equipment = [];

      if (value !== 2) {
        form.values.handlingType = 0;
      }
    }

    return form.setFieldValue(name, value, true);
  };

  const isTruckSelected = useMemo(() => {
    if (!values.equipmentType) return false;

    return equipmentTypes.some(
      e => e.id === values.equipmentType && e.id === 2,
    );
  }, [values.equipmentType, equipmentTypes]);

  const renderHandlingTypes = () => {
    if (!isTruckSelected) {
      return [renderMenuItem('handlingtypes_0', 0, translate('common:Select'))];
    }

    return [
      renderMenuItem('handlingtypes_0', 0, translate('common:Select')),
      ...handlingTypes.map(e => renderMenuItem(e.id, e.id, e.name, e.active)),
    ];
  };

  const handleSearchDailyParts = (form, value) => {
    setSearchDailyParts(value);
    form.setFieldValue('searchDailyParts', value);
  };

  const isFieldDisabled = value => (!(value > 0));

  const renderSelect = (name, placeholder, children, disabled) => (
    <Fragment>
      <Field
        name={name}
        render={({ field, form }) => (
          <PF2MSearchSelectOutlined
            disabled={disabled}
            placeholder={placeholder}
            {...field}
            onChange={(e) => {
              validateSelection({
                form,
                name: field.name,
                value: e.target.value,
              });
            }}
            style={{ height: 31, width: '100%' }}
          >
            {children}
          </PF2MSearchSelectOutlined>
        )}
      />
      <ErrorMessage
        name={name}
        render={msg => (
          <Typography
            color="error"
            variant="caption"
          >
            {msg}
          </Typography>
        )}
      />
    </Fragment>
  );
  const renderMultiSelect = ({ name, children, disabled }) => (
    <Field name={name}>
      {({ field, form }) => {
        const options = Array.isArray(children) && children[0]?.value
          ? children
          : React.Children.toArray(children)
            .filter(child => React.isValidElement(child) && child.props)
            .map(child => ({
              label: child.props.children || child.props.label || '',
              value: child.props.value,
              inactiveCondition: child.props.active === false,
              tooltip: child.props.active === false
                ? translate('common:Inactive')
                : '',
            }));

        return (
          <div style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
          >
            <PF2MAutocomplete
              title={''}
              placeholder={translate('common:Select')}
              options={options}
              disabled={disabled}
              value={options.filter(opt => (Array.isArray(field.value)
                ? field.value.includes(opt.value)
                : field.value === opt.value))}
              onChange={(event, newValue) => {
                form.setFieldValue(
                  field.name,
                  newValue ? newValue.map(item => item.value) : [],
                );
              }}
              limitTags={2}
              style={{
                width: '100%',
                flex: 1,
                '& .MuiAutocomplete-inputRoot': {
                  flexWrap: 'wrap',
                  minHeight: '56px',
                  padding: '6px 12px',
                  alignItems: 'flex-start',
                },
                '& .MuiAutocomplete-tag': {
                  margin: '2px',
                  maxWidth: 'calc(50% - 4px)',
                },
              }}
            />
            <ErrorMessage
              name={name}
              render={msg => (
                <Typography color="error" variant="caption">
                  {msg}
                </Typography>
              )}
            />
          </div>
        );
      }}
    </Field>
  );

  const YellowCheckbox = withStyles({
    root: {
      color: '#FFC300',
      '&$checked': {
        color: '#FFC300',
      },
    },
    checked: {},
  })(props => <Checkbox color="default" {...props} />);

  const renderCheckbox = (name, label, disabled) => (
    <Field name={name}>
      {({ field, form }) => (
        <FormControlLabel
          style={{ marginLeft: 0, gap: 8 }}
          control={(
            <YellowCheckbox
              disabled={disabled}
              checked={field.value}
              onChange={() => form.setFieldValue(field.name, !field.value)}
            />
          )}
          label={label}
        />
      )}
    </Field>
  );

  const defaultFilterValues = {
    startDate: newMomentDate().startOf('day').toDate(),
    endDate: newMomentDate().endOf('day').toDate(),
    turn: 0,
    equipmentType: 0,
    equipmentGroup: [],
    equipment: [],
    operatorGroup: 0,
    operator: 0,
    material: 0,
    startOrigin: 0,
    endOrigin: 0,
    handlingType: 0,
    hideInterruptedOrDeleted: false,
    showOnlyReservedEquips: false,
    searchDailyParts: true,
  };

  const renderFormContent = () => (
    <Grid container>
      <Grid item xs={3}>
        <Field
          name={'setSearchDailyParts'}
          render={({ form }) => (
            <Grid
              container
              spacing={0}
              className={classes.dailyPartsSelection}
            >
              <Grid item xs={4}>
                <PF2MButton
                  className={
                    !searchDailyParts ? classes.unSelectedButton : ''
                  }
                  onClick={() => handleSearchDailyParts(form, true)}
                >
                  {translate('common:DailyParts')}
                </PF2MButton>
              </Grid>
              <Grid item xs={6}>
                <PF2MButton
                  className={
                    searchDailyParts ? classes.unSelectedButton : ''
                  }
                  onClick={() => handleSearchDailyParts(form, false)}
                >
                  {translate('common:CodeMigrations')}
                </PF2MButton>
              </Grid>
            </Grid>
          )}
        />
      </Grid>
      <Grid item xs={6}>
        <Card>
          <CardHeader icon style={{ background: 'transparent' }}>
            <>
              {isModal && (
                <CardIcon
                  onClick={onClose}
                  style={{
                    float: 'right',
                    cursor: 'pointer',
                    background: 'darkgray',
                    borderRadius: 50,
                    boxShadow: '10px 10px 50px gray',
                  }}
                >
                  <CloseIcon />
                </CardIcon>
              )}
            </>
          </CardHeader>
          <CardBody>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
                mt: isModal ? 0 : 4,
              }}
            >
              <CardIcon
                style={{
                  float: 'right',
                  cursor: 'pointer',
                  borderRadius: 8,
                  color: 'white',
                  padding: 4,
                  background: '#FFC300',
                  boxShadow: 'none',
                }}
              >
                <SearchIcon fontSize="large" />
              </CardIcon>
              <Typography variant="h4" align="center" gutterBottom>
                {translate('common:Search')}
              </Typography>
            </Box>

            <Grid container spacing={2} className={classes.fieldsContainer}>
              <Grid item container spacing={4} xs={12}>
                <Grid item xs>
                  <span className={classes.labelSpan}>
                    {translate('common:StartDateTime')}
                  </span>
                  {renderDate('startDate')}
                </Grid>
                <Grid item xs>
                  <span className={classes.labelSpan}>
                    {translate('common:EndDateTime')}
                  </span>
                  {renderDate('endDate')}
                </Grid>
                <Grid item xs>
                  <span className={classes.labelSpan}>
                    {translate('common:EquipmentType')}
                  </span>
                  {renderSelect(
                    'equipmentType',
                        `${translate('common:Type')}*`,
                        renderEquipmentTypes(),
                  )}
                </Grid>
              </Grid>

              <Grid item container spacing={4} xs={12}>
                <Grid item xs>
                  <Divider className={classes.divider} />
                </Grid>
              </Grid>

              <Grid item container spacing={4} xs={12}>
                <Grid item xs={12} sm={4}>
                  <span className={classes.labelSpan}>
                    {translate('common:EquipmentGroup')}
                  </span>
                  {renderMultiSelect({
                    name: 'equipmentGroup',
                    placeholder: translate('common:Group'),
                    children: renderEquipmentGroups(),
                    disabled: isFieldDisabled(values.equipmentType),
                  })}
                </Grid>

                <Grid item xs={12} sm={4}>
                  <span className={classes.labelSpan}>
                    {translate('common:Equipment')}
                  </span>
                  {renderMultiSelect({
                    name: 'equipment',
                    placeholder: translate('common:Equipment'),
                    children: renderEquipments(),
                    disabled: isFieldDisabled(values.equipmentType),
                  })}
                </Grid>

                <Grid item xs={12} sm={4}>
                  <span className={classes.labelSpan}>
                    {translate('common:Shift')}
                  </span>
                  {renderSelect(
                    'turn',
                    translate('common:Shift'),
                    renderTurns(),
                    !searchDailyParts,
                  )}
                </Grid>
              </Grid>

              <Grid item container spacing={4} xs={12}>
                <Grid item xs>
                  <span className={classes.labelSpan}>
                    {translate('common:Material')}
                  </span>
                  {renderSelect(
                    'material',
                    translate('common:Material'),
                    renderMaterials(),
                    !searchDailyParts,
                  )}
                </Grid>
                <Grid item xs>
                  <span className={classes.labelSpan}>
                    {translate('common:OriginElement')}
                  </span>
                  {renderSelect(
                    'startOrigin',
                    translate('common:OriginElement'),
                    renderOrigins(),
                    !searchDailyParts,
                  )}
                </Grid>
                <Grid item xs>
                  <span className={classes.labelSpan}>
                    {translate('common:OriginSubElement')}
                  </span>
                  {renderSelect(
                    'startSubOrigin',
                    translate('common:OriginSubElement'),
                    renderSubOrigins('startOrigin'),
                    !searchDailyParts,
                  )}
                </Grid>
              </Grid>

              <Grid item container spacing={4} xs={12}>
                <Grid item xs md={4}>
                  <span className={classes.labelSpan}>
                    {translate('common:DestinationElement')}
                  </span>
                  {renderSelect(
                    'endOrigin',
                    translate('common:DestinationElement'),
                    renderOrigins(),
                    !searchDailyParts,
                  )}
                </Grid>
                <Grid item xs md={4}>
                  <span className={classes.labelSpan}>
                    {translate('common:DestinationSubElement')}
                  </span>
                  {renderSelect(
                    'endSubOrigin',
                    translate('common:DestinationSubElement'),
                    renderSubOrigins('endOrigin'),
                    !searchDailyParts,
                  )}
                </Grid>
                {isTruckSelected
                  && (
                    <Grid item xs md={4}>
                      <span className={classes.labelSpan}>
                        {translate('common:MovementType')}
                      </span>
                      {renderSelect(
                        'handlingType',
                        translate('common:MovementType'),
                        renderHandlingTypes(),
                        !searchDailyParts,
                      )}
                    </Grid>
                  )
                }
              </Grid>

              <Grid item container spacing={4} xs={12}>
                <Grid item xs>
                  <span className={classes.labelSpan}>
                    {translate('common:CodeType')}
                  </span>
                  {renderSelect(
                    'codeType',
                    translate('common:CodeType'),
                    renderCodesTypes(),
                    !searchDailyParts,
                  )}
                </Grid>
                <Grid item xs>
                  <span className={classes.labelSpan}>
                    {translate('common:CodeGroup')}
                  </span>
                  {renderSelect(
                    'codeGroup',
                    translate('common:CodeGroup'),
                    renderCodeGroups(),
                    !searchDailyParts,
                  )}
                </Grid>
                <Grid item xs>
                  <span className={classes.labelSpan}>
                    {translate('common:Code')}
                  </span>
                  {renderSelect(
                    'code',
                    translate('common:Code'),
                    renderCodes(),
                    !searchDailyParts,
                  )}
                </Grid>
              </Grid>

              <Grid item container spacing={4} xs={12}>
                <Grid item xs md={4}>
                  <span className={classes.labelSpan}>
                    {translate('common:OperatorGroup')}
                  </span>
                  {renderSelect(
                    'operatorGroup',
                    translate('common:Group'),
                    renderOperatorsGroups(),
                    !searchDailyParts,
                  )}
                </Grid>
                <Grid item xs md={4}>
                  <span className={classes.labelSpan}>
                    {translate('common:Operator')}
                  </span>
                  {renderSelect(
                    'operator',
                    translate('common:Operator'),
                    renderOperators(),
                    !searchDailyParts,
                  )}
                </Grid>
              </Grid>

              <Grid item container spacing={4} xs={12}>
                <Grid item xs>
                  <Divider className={classes.divider} />
                </Grid>
              </Grid>

              <Grid item container spacing={2} xs>
                <Grid item xs md={12}>
                  {renderCheckbox(
                    'hideInterruptedOrDeleted',
                    translate('common:HideInterruptedOrDeletedCycles'),
                    !searchDailyParts,
                  )}
                </Grid>
                {Number(enableShowOnlyReservedEquips) === 1 ? (
                  <Grid item xs md={12}>
                    {renderCheckbox(
                      'showOnlyReservedEquips',
                      translate('common:ShowOnlyReservedEquips'),
                      !searchDailyParts,
                    )}
                  </Grid>
                ) : null}
              </Grid>

              <Grid item container spacing={4} xs={12}>
                <Grid item xs>
                  <Divider className={classes.divider} />
                </Grid>
              </Grid>

              <Grid item className={classes.buttonContainer}>
                <PF2MClearFiltersButton
                  onClick={() => resetForm(defaultFilterValues)}
                  style={{
                    width: '100%',
                    maxWidth: '200px',
                    color: '#A3A3A3',
                    border: '2px solid #A3A3A3',
                    background: 'transparent',
                    boxShadow: 'none',
                  }}
                />
                <PF2MSearchButton
                  onClick={handleSubmit}
                  disabled={!isValid || isFieldDisabled(values.equipmentType)}
                  style={{
                    width: '100%',
                    maxWidth: '200px',
                    boxShadow: 'none',
                  }}
                />
              </Grid>
            </Grid>
          </CardBody>
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <Form className={classes.form}>
      {isModal
        ? (
          <Dialog
            open={open}
            maxWidth="xl"
            fullWidth
            fullScreen={fullScreen}
            scroll="body"
            PaperProps={{
              style: {
                backgroundColor: 'transparent',
                boxShadow: 'none',
              },
            }}
            BackdropProps={{
              style: {
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
              },
            }}
          >
            {renderFormContent()}
          </Dialog>
        )
        : renderFormContent()}
    </Form>
  );
};

const enhanceWithFormik = withFormik({
  mapPropsToValues: ({ filters }) => ({
    startDate: filters.startDate ? filters.startDate : newMomentDate().startOf('day').toDate(),
    endDate: filters.endDate ? filters.endDate : newMomentDate().endOf('day').toDate(),
    turn: filters.turn || 0,
    equipmentType: filters.equipmentType || 0,
    equipmentGroup: filters.equipmentGroup || [],
    equipment: filters.equipment || [],
    operatorGroup: filters.operatorGroup || 0,
    operator: filters.operator || 0,
    material: filters.material || 0,
    startOrigin: filters.startOrigin || 0,
    endOrigin: filters.endOrigin || 0,
    startSubOrigin: filters.startSubOrigin || 0,
    endSubOrigin: filters.endSubOrigin || 0,
    codeType: filters.codeType || 0,
    codeGroup: filters.codeGroup || 0,
    code: filters.code || 0,
    handlingType: filters.handlingType || 0,
    searchDailyParts: filters.searchDailyParts || true,
    hideInterruptedOrDeleted: Boolean(filters.hideInterruptedOrDeleted),
    showOnlyReservedEquips: Boolean(filters.showOnlyReservedEquips),
  }),
  handleSubmit: async (values, { props }) => {
    props.resetDirtyFlags();

    props.setFilters({
      ...values,
      startDate: moment(values.startDate),
      endDate: moment(values.endDate),
    });

    if (!values.searchDailyParts) {
      props.history.push('/dynamic');
      return;
    }

    if (!props.isModal) {
      props.history.push('/parts/list');
    } else {
      props.onSearch();
    }
  },
  validateOnChange: true,
  validateOnBlur: true,
  isInitialValid: true,
  displayName: 'SearchForm',
  validate: ({
    equipmentType,
    startDate,
    endDate,
  }) => {
    const startDateMoment = moment(startDate);
    const endDateMoment = moment(endDate);
    const errors = {};

    if (!startDateMoment.isValid()) {
      errors.startDate = i18n.t('validation:InitialDateRequired');
    }

    if (!endDateMoment.isValid()) {
      errors.endDate = i18n.t('validation:FinalDateRequired');
    }

    if (startDateMoment.isValid() && endDateMoment.isValid()) {
      const days = endDateMoment.diff(startDateMoment, 'days');
      const searchLimit = process.env.REACT_APP_SEARCH_LIMIT_IN_DAYS || 7;

      if (days > searchLimit) {
        const searchTimeRangeMsg = i18n.t('validation:SearchTimeRangeCantExceed');
        const daysMsg = i18n.t('common:Days').toLowerCase();
        errors.endDate = `${searchTimeRangeMsg} ${searchLimit} ${daysMsg}.`;
      }

      if (endDateMoment.isBefore(startDateMoment)) {
        errors.endDate = i18n.t('validation:FinalDateSmallerThanInitialDate');
      }
    }

    const errorMessage = i18n.t('validation:RequiredField');

    if (!equipmentType) {
      errors.equipmentType = errorMessage;
    }

    return errors;
  },
})(SearchForm);

SearchForm.propTypes = {
  classes: PropTypes.object.isRequired,
  values: PropTypes.object,
  handleSubmit: PropTypes.func,
  isValid: PropTypes.bool,
  equipmentTypes: PropTypes.array,
  turns: PropTypes.array,
  equipmentGroups: PropTypes.array,
  equipments: PropTypes.array,
  operatorsGroups: PropTypes.array,
  operators: PropTypes.array,
  materials: PropTypes.array,
  elements: PropTypes.array,
  subElements: PropTypes.array,
  codeGroups: PropTypes.array,
  codes: PropTypes.array,
  isModal: PropTypes.bool,
  onClose: PropTypes.func,
  resetForm: PropTypes.func,
  open: PropTypes.bool,
};

SearchForm.defaultProps = {
  values: {},
  handleSubmit: {},
  isValid: false,
  equipmentTypes: [],
  turns: [],
  equipmentGroups: [],
  equipments: [],
  operatorsGroups: [],
  operators: [],
  materials: [],
  elements: [],
  subElements: [],
  codeGroups: [],
  codes: [],
  isModal: false,
  onClose: null,
  resetForm: null,
  open: false,
};

const mapStateToProps = state => ({
  filters: state.dailyParts.filters,
  equipmentTabs: state.dailyParts.equipmentTabs,
  equipmentTypes: state.manager.equipmentTypes,
  turns: state.manager.turns,
  equipmentGroups: state.manager.equipmentsGroups,
  equipments: state.manager.equipments,
  operatorsGroups: state.manager.operatorsGroups,
  operators: state.manager.operators,
  materials: state.manager.materials,
  elements: state.manager.elements,
  subElements: state.manager.subElements,
  codeGroups: state.manager.codeGroups,
  codes: state.manager.codes,
  handlingTypes: state.manager.handlingTypes,
});

const mapDispatchToProps = {
  searchTransportList,
  setFilters,
  resetDirtyFlags,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles)(enhanceWithFormik));
